/** Fetching a filing's PDF with a state a person can see (gate 24, ADR-0078).
 *
 * The PDF route used to be a plain link in a new tab. That is right when it works and bad when it
 * does not: a render takes a few seconds with nothing on screen to say so, and a refusal arrives as
 * the API's raw JSON on a blank tab (`{"detail":{"error":"renderer_timeout",...}}`), which is the
 * least helpful thing a reader can be shown for a filing they came to print.
 *
 * So a plain click fetches it here - "Preparing the PDF…" while it renders, the file saved when it
 * arrives, a sentence in words when it does not - and **the link is still a link**: a modified click
 * (new tab, new window, save-as) is left to the browser, which reaches the same route. Nothing here
 * changes what the route does or what is asked of it; the PDF is still a view drawn on request from
 * the stored record, never stored by the screen.
 *
 * **One sentence per cause, and none of them quotes the server's internals.** The route's `message`
 * is written for a log; "Chromium did not start" tells a customer nothing they can act on and
 * suggests a fault with their filing, which there is not. The server's own sentence is kept for the
 * two refusals that are statements about the *filing* (`stored_record_altered`) or the
 * *deployment* (`pdf_not_configured`), where it is the fact and a paraphrase would be a second home
 * for it.
 */

import { useCallback, useRef, useState } from "react";

import { apiFetch, apiUrl } from "./client";

/** The API's gateway gives up at 30 s; the renderer's own limit is below that. A little longer than
 * both, so the answer the server gave - rather than our own giving up - is what the reader sees. */
export const PDF_REQUEST_TIMEOUT_MS = 40_000;

export type PdfPhase = "idle" | "preparing" | "saved" | "failed";

export interface PdfState {
  phase: PdfPhase;
  /** The sentence to show when `phase` is `failed`; empty otherwise. */
  message: string;
  /** The API's own code, or `""` (a network failure, a timeout of ours) - for tests and support. */
  code: string;
}

const IDLE: PdfState = { phase: "idle", message: "", code: "" };

/** What the reader is told, per cause. Exported so a test can hold every cause to a sentence.
 *
 * `serverMessage` is used verbatim for the two refusals that are facts about the filing or the
 * deployment. Everything else is ours, and each ends with what is **not** affected: a failed PDF is
 * never a statement about the filing, which is intact in its page, JSON and CSV. */
export function pdfFailureMessage(status: number, code: string, serverMessage: string): string {
  if (code === "pdf_not_configured" && serverMessage) return serverMessage;
  if (code === "stored_record_altered" && serverMessage) return serverMessage;
  const unaffected = "The filing itself is unaffected: its page, JSON and CSV still work.";
  if (status === 401) return "Your session has ended. Sign in again, then try the PDF again.";
  if (status === 403) return "Your role may not print this filing.";
  if (status === 404) return "This filing could not be found, so there is nothing to print.";
  if (
    status === 413 ||
    code === "request_too_large" ||
    code === "too_large_no_store" ||
    code === "render_too_large"
  ) {
    return `This filing is too large to print as a PDF here. ${unaffected} Open the page and print that instead.`;
  }
  if (status === 504 || code === "render_timeout" || code === "renderer_timeout") {
    return `The PDF took too long to prepare. Try again in a moment. ${unaffected}`;
  }
  if (status === 503) {
    return `The PDF service is unavailable right now, so no PDF was made. Try again in a minute. ${unaffected}`;
  }
  return `The PDF could not be made. Try again; if it keeps happening, tell us the time it happened. ${unaffected}`;
}

/** The file name the server suggested (`emitiq-filing-<digest>.pdf`, `SAMPLE-` first for a sample
 * workspace) - the one place a sample marker travels with the file. A header that is missing or odd
 * is replaced by a name that still says what it is. */
export function filenameFrom(header: string | null): string {
  const quoted = header ? /filename="([^"\\/]+)"/.exec(header) : null;
  return quoted?.[1] ?? "emitiq-filing.pdf";
}

function save(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // After the click has been handled: revoking synchronously can cancel the save in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** `{ state, start }` for one filing's PDF. `start` ignores a press while one is in flight, so a
 * double click is one render and not two Chromium launches. */
export function usePdfDownload(artifactId: string) {
  const [state, setState] = useState<PdfState>(IDLE);
  const busy = useRef(false);
  const path = `/api/v1/artifacts/${artifactId}/pdf`;

  const start = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setState({ phase: "preparing", message: "", code: "" });
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), PDF_REQUEST_TIMEOUT_MS);
    try {
      // `manual`: a PDF over the inline ceiling is answered with a 303 to a short-lived link on a
      // bucket this page cannot read cross-origin. That is not a failure; it is handed to the
      // browser, which can follow it as a navigation.
      const response = await apiFetch(path, { redirect: "manual", signal: controller.signal });
      if (response.type === "opaqueredirect") {
        window.open(apiUrl(path), "_blank", "noopener,noreferrer");
        setState({ phase: "saved", message: "", code: "" });
        return;
      }
      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        const detail =
          body && typeof body === "object" && "detail" in body
            ? (body).detail
            : null;
        const fields = detail && typeof detail === "object" ? (detail as Record<string, unknown>) : {};
        const code = typeof fields.error === "string" ? fields.error : "";
        const message = typeof fields.message === "string" ? fields.message : "";
        setState({
          phase: "failed",
          code,
          message: pdfFailureMessage(response.status, code, message),
        });
        return;
      }
      const blob = await response.blob();
      if (!(response.headers.get("content-type") ?? "").includes("application/pdf")) {
        setState({
          phase: "failed",
          code: "not_a_pdf",
          message: pdfFailureMessage(502, "not_a_pdf", ""),
        });
        return;
      }
      save(blob, filenameFrom(response.headers.get("content-disposition")));
      setState({ phase: "saved", message: "", code: "" });
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === "AbortError";
      setState({
        phase: "failed",
        code: aborted ? "client_timeout" : "network",
        message: aborted
          ? pdfFailureMessage(504, "client_timeout", "")
          : "The PDF could not be requested. Check your connection and try again. The filing itself is unaffected.",
      });
    } finally {
      window.clearTimeout(timer);
      busy.current = false;
    }
  }, [path]);

  return { state, start };
}
